import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { supabase } from '../config/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

export default function InviteModal({ show, onClose }) {
  const [email, setEmail] = useState('');
  const [inviteType, setInviteType] = useState('link'); // 'email' or 'link'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const { session, user } = useAuth();

  // Generate invite link
  const generateInviteLink = () => {
    return `https://tennisnowapp.com`;
  };

  const copyInviteLink = async () => {
    const link = generateInviteLink();
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (err) {
        setError('Unable to copy link. Please try manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsSubmitting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-invite-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            email,
            message: undefined
          })
        }
      );

      const responseData = await response.json();
      console.log('Edge Function raw response:', response.status, responseData);

      if (!response.ok) {
        if (responseData.error === 'ALREADY_INVITED') {
          setError('This email has already been invited to ServeUp. Ask them to check their inbox!');
        } else if (responseData.error === 'ALREADY_REGISTERED') {
          setError('This tennis partner is already on ServeUp! Try inviting someone else.');
        } else if (responseData.error === 'RATE_LIMIT') {
          setError('Please wait a few minutes before sending another invite.');
        } else {
          setError(responseData.message || 'Unable to send invitation. Please try again.');
        }
        return;
      }

      setEmail('');
      setSuccess(true);
      
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);

    } catch (error) {
      console.error('Error sending invite:', error);
      setError('Unable to send invitation. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 max-w-md w-full shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-primary w-full text-center">Invite Tennis Partner</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors absolute right-8"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-8 text-center">
          <div className="text-4xl mb-4">🎾</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Grow Your Tennis Circle
          </h3>
          <p className="text-gray-600">
            Invite your favorite tennis partner to join ServeUp and enjoy playing together!
          </p>
        </div>

        {/* Invite Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            How would you like to invite them?
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                value="email"
                checked={inviteType === 'email'}
                onChange={(e) => setInviteType(e.target.value)}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700 flex items-center">
                📧 Send Email
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                value="link"
                checked={inviteType === 'link'}
                onChange={(e) => setInviteType(e.target.value)}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700 flex items-center">
                📱 Share Link
              </span>
            </label>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-100 text-green-700 rounded-lg">
            Invitation sent successfully!
          </div>
        )}

        {/* Conditional Content Based on Selection */}
        {inviteType === 'email' ? (
          // Email form
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="email">
                Partner's Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter their email address"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                required
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primaryDark transition-colors disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send Invitation'
                )}
              </button>
            </div>
          </form>
        ) : (
          // Link sharing section
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Invite Link
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={generateInviteLink()}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-600"
                />
                <button
                  type="button"
                  onClick={copyInviteLink}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    linkCopied 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-primary text-white hover:bg-primaryDark'
                  }`}
                >
                  {linkCopied ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-start">
                <div className="text-blue-500 text-xl mr-3">💡</div>
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-1">
                    How to share this link:
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Text it to your tennis partner</li>
                    <li>• Post in your tennis group chat</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

InviteModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
}; 